import React from 'react';

// Main Animated Car Loader - Car moves left to right
const CarLoader = ({ size = 'default', message = 'Loading...' }) => {
  const sizeConfig = {
    small: { container: 'w-48 h-24', car: 'w-16 h-10' },
    default: { container: 'w-72 h-32', car: 'w-28 h-16' },
    large: { container: 'w-96 h-40', car: 'w-36 h-22' }
  };

  return (
    <div className="flex flex-col items-center justify-center py-6">
      {/* Animation Container */}
      <div className={`relative ${sizeConfig[size].container} overflow-hidden`}>
        {/* Premium Road Background */}
        <div className="absolute bottom-0 left-0 right-0 h-4">
          {/* Road base - lighter asphalt */}
          <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-b from-gray-400 via-gray-500 to-gray-600 rounded-sm shadow-md">
            {/* Road texture overlay */}
            <div className="absolute inset-0 opacity-30 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>

            {/* White edge line - top */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-90"></div>

            {/* White edge line - bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-90"></div>

            {/* Center line markings - dashed white/yellow */}
            <div className="road-markings absolute inset-0 flex items-center justify-center overflow-hidden">
              <div className="flex space-x-6 animate-road-scroll">
                {[...Array(16)].map((_, i) => (
                  <span key={i} className="w-6 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 rounded-sm flex-shrink-0"></span>
                ))}
              </div>
            </div>
          </div>

          {/* Road shoulder/edge */}
          <div className="absolute bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300"></div>
        </div>

        {/* Car Container - Moves left to right */}
        <div className={`absolute bottom-3 ${sizeConfig[size].car} animate-car-drive`}>
          {/* Car Shadow */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-2 bg-black/20 rounded-full blur-sm"></div>

          {/* Classic Ambassador - Vintage Indian Car */}
          <svg
            viewBox="-20 0 220 95"
            className="w-full h-full drop-shadow-2xl animate-car-bounce"
            fill="none"
          >
            <defs>
              {/* Classic Black Paint */}
              <linearGradient id="ambassadorPaint" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#374151" />
                <stop offset="30%" stopColor="#1F2937" />
                <stop offset="60%" stopColor="#111827" />
                <stop offset="100%" stopColor="#030712" />
              </linearGradient>

              {/* Chrome Gradient */}
              <linearGradient id="chromeGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#F8FAFC" />
                <stop offset="30%" stopColor="#E2E8F0" />
                <stop offset="50%" stopColor="#94A3B8" />
                <stop offset="100%" stopColor="#64748B" />
              </linearGradient>

              {/* Window Glass */}
              <linearGradient id="vintageGlass" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#CBD5E1" />
                <stop offset="50%" stopColor="#94A3B8" />
                <stop offset="100%" stopColor="#64748B" />
              </linearGradient>

              {/* Black Tire */}
              <radialGradient id="vintageTire" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#374151" />
                <stop offset="70%" stopColor="#1F2937" />
                <stop offset="100%" stopColor="#111827" />
              </radialGradient>

              {/* Whitewall */}
              <radialGradient id="whitewall" cx="50%" cy="50%" r="50%">
                <stop offset="70%" stopColor="#F1F5F9" />
                <stop offset="100%" stopColor="#E2E8F0" />
              </radialGradient>

              {/* Classic Headlight */}
              <radialGradient id="classicLight" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FFFBEB" />
                <stop offset="50%" stopColor="#FEF3C7" />
                <stop offset="100%" stopColor="#FCD34D" />
              </radialGradient>

              {/* Shadow */}
              <linearGradient id="groundShadow" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(0,0,0,0)" />
                <stop offset="50%" stopColor="rgba(0,0,0,0.25)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0)" />
              </linearGradient>
            </defs>

            {/* Ground Shadow */}
            <ellipse cx="95" cy="86" rx="80" ry="6" fill="url(#groundShadow)" />

            {/* === AMBASSADOR BODY === */}

            {/* Main Body - Distinct Front Hood and Rear Trunk */}
            <path
              d="M15 58
                 L15 52
                 Q16 48 22 46
                 L28 44
                 L45 42
                 L130 42
                 L145 38
                 L158 36
                 L168 42
                 Q172 46 172 52
                 L172 58
                 L172 68
                 L15 68
                 L15 58 Z"
              fill="url(#ambassadorPaint)"
              stroke="#4B5563"
              strokeWidth="1"
            />

            {/* Lower Body Panel */}
            <rect x="15" y="62" width="157" height="6" fill="#030712" />

            {/* Front Hood/Bonnet - Long Slope */}
            <path
              d="M130 42 L145 38 L160 35 L170 40 L168 42 L145 38 L130 42"
              fill="url(#ambassadorPaint)"
              stroke="#4B5563"
              strokeWidth="0.5"
            />

            {/* Rear Boot/Trunk - Shorter, Curved */}
            <path
              d="M45 42 L28 44 L18 50 L15 56"
              fill="none"
              stroke="#4B5563"
              strokeWidth="0.5"
            />

            {/* Front Fender - More Prominent */}
            <path
              d="M155 48 Q165 45 170 50 Q172 55 172 62"
              fill="url(#ambassadorPaint)"
              stroke="#4B5563"
              strokeWidth="0.5"
            />

            {/* Rear Fender - Rounded */}
            <path
              d="M30 52 Q20 50 16 54 Q15 58 15 62"
              fill="url(#ambassadorPaint)"
              stroke="#4B5563"
              strokeWidth="0.5"
            />

            {/* === CABIN / ROOF === */}
            {/* Rounded Roof - Classic Ambassador Style with distinct front/rear */}
            <path
              d="M48 42
                 Q48 32 54 24
                 L62 18
                 L118 18
                 L128 26
                 Q132 34 130 42"
              fill="url(#ambassadorPaint)"
              stroke="#4B5563"
              strokeWidth="1"
            />

            {/* Roof Top */}
            <path
              d="M58 22 Q95 16 125 24"
              fill="none"
              stroke="#030712"
              strokeWidth="0.5"
            />

            {/* === WINDOWS === */}
            {/* Rear Windshield - More Upright (back of car) */}
            <path
              d="M50 40 L55 24 L82 22 L82 40 Z"
              fill="url(#vintageGlass)"
              stroke="#94A3B8"
              strokeWidth="1"
            />

            {/* Front Windshield - More Sloped (front of car) */}
            <path
              d="M92 22 L120 24 L128 40 L92 40 Z"
              fill="url(#vintageGlass)"
              stroke="#94A3B8"
              strokeWidth="1"
            />

            {/* Side Window - Rear */}
            <rect x="52" y="26" width="26" height="12" rx="1" fill="url(#vintageGlass)" stroke="#94A3B8" strokeWidth="0.5" />

            {/* Side Window - Front */}
            <rect x="95" y="26" width="28" height="12" rx="1" fill="url(#vintageGlass)" stroke="#94A3B8" strokeWidth="0.5" />

            {/* Window Divider (B-Pillar) */}
            <rect x="83" y="20" width="7" height="22" fill="url(#ambassadorPaint)" stroke="#4B5563" strokeWidth="0.5" />

            {/* === DRIVER === */}
            {/* Driver visible through front side window */}
            <g>
              {/* Driver Head */}
              <ellipse cx="108" cy="28" rx="5" ry="6" fill="#8B5CF6" />
              {/* Hair */}
              <ellipse cx="108" cy="24" rx="5" ry="3" fill="#1F2937" />
              {/* Face details */}
              <circle cx="110" cy="27" r="0.8" fill="#1F2937" /> {/* Eye */}
              {/* Ear */}
              <ellipse cx="113" cy="28" rx="1" ry="1.5" fill="#A78BFA" />

              {/* Driver Body/Shoulders */}
              <path
                d="M100 36 Q104 32 108 34 Q112 32 116 36 L116 42 L100 42 Z"
                fill="#3B82F6"
              />

              {/* Shirt collar */}
              <path
                d="M105 34 L108 36 L111 34"
                fill="none"
                stroke="#1E40AF"
                strokeWidth="0.5"
              />

              {/* Arms holding steering wheel */}
              <path
                d="M102 38 Q106 40 110 38"
                fill="none"
                stroke="#8B5CF6"
                strokeWidth="2"
                strokeLinecap="round"
              />

              {/* Steering wheel glimpse */}
              <circle cx="115" cy="42" r="6" fill="none" stroke="#374151" strokeWidth="1.5" opacity="0.6" />
              <circle cx="115" cy="42" r="2" fill="#374151" opacity="0.6" />
            </g>

            {/* === CHROME DETAILS === */}

            {/* Front Chrome Bumper - Large Classic Style */}
            <rect x="165" y="60" width="12" height="6" rx="2" fill="url(#chromeGrad)" />
            <rect x="167" y="62" width="8" height="2" fill="#F8FAFC" />

            {/* Rear Chrome Bumper - Smaller */}
            <rect x="8" y="60" width="10" height="5" rx="2" fill="url(#chromeGrad)" />
            <rect x="10" y="62" width="6" height="2" fill="#F8FAFC" />

            {/* Chrome Side Strip */}
            <rect x="30" y="52" width="130" height="2" rx="1" fill="url(#chromeGrad)" />

            {/* Chrome Window Trim */}
            <path d="M50 40 L130 40" stroke="url(#chromeGrad)" strokeWidth="2" />

            {/* Door Handles - Chrome */}
            <rect x="68" y="48" width="8" height="3" rx="1.5" fill="url(#chromeGrad)" />
            <rect x="105" y="48" width="8" height="3" rx="1.5" fill="url(#chromeGrad)" />

            {/* === FRONT GRILLE - Classic Ambassador with Vertical Bars === */}
            <rect x="158" y="44" width="14" height="18" rx="2" fill="#1F2937" />
            {/* Horizontal Chrome Bars - Classic Ambassador Style */}
            <rect x="159" y="46" width="12" height="2" fill="url(#chromeGrad)" />
            <rect x="159" y="50" width="12" height="2" fill="url(#chromeGrad)" />
            <rect x="159" y="54" width="12" height="2" fill="url(#chromeGrad)" />
            <rect x="159" y="58" width="12" height="2" fill="url(#chromeGrad)" />

            {/* Ambassador Logo/Emblem on Hood */}
            <circle cx="155" cy="38" r="4" fill="url(#chromeGrad)" />
            <circle cx="155" cy="38" r="2" fill="#4B5563" />
            {/* Hood Ornament */}
            <ellipse cx="150" cy="37" rx="3" ry="2" fill="url(#chromeGrad)" />

            {/* === HEADLIGHTS - Large Round Classic === */}
            {/* Main Headlight */}
            <circle cx="168" cy="52" r="7" fill="#1F2937" stroke="url(#chromeGrad)" strokeWidth="2" />
            <circle cx="168" cy="52" r="5" fill="url(#classicLight)" className="animate-headlight" />
            <circle cx="168" cy="52" r="2.5" fill="#FFFBEB" />

            {/* Headlight Beam Effect */}
            <path
              d="M175 48 L195 42 L195 62 L175 56"
              fill="rgba(254,243,199,0.25)"
              className="animate-headlight"
            />

            {/* Indicator Light - Orange */}
            <circle cx="172" cy="62" r="3" fill="#F59E0B" stroke="url(#chromeGrad)" strokeWidth="1" />

            {/* === TAIL LIGHTS - Classic Round Red === */}
            {/* Main Tail Light */}
            <circle cx="18" cy="52" r="6" fill="#7F1D1D" stroke="url(#chromeGrad)" strokeWidth="2" />
            <circle cx="18" cy="52" r="4" fill="#EF4444" className="animate-pulse" />
            <circle cx="18" cy="52" r="2" fill="#FCA5A5" />

            {/* Backup/Reverse Light - White */}
            <rect x="14" y="60" width="8" height="4" rx="1" fill="#E5E7EB" stroke="url(#chromeGrad)" strokeWidth="0.5" />

            {/* Rear Reflector */}
            <rect x="24" y="55" width="4" height="6" rx="1" fill="#DC2626" />

            {/* === SIDE MIRROR - Classic Round (Near Front Windshield) === */}
            <ellipse cx="132" cy="36" rx="4" ry="3" fill="url(#chromeGrad)" />
            <ellipse cx="132" cy="36" rx="2.5" ry="2" fill="#94A3B8" />

            {/* === DOOR LINES === */}
            <line x1="86" y1="42" x2="86" y2="62" stroke="#4B5563" strokeWidth="1" />
            {/* Second door line */}
            <line x1="70" y1="42" x2="70" y2="62" stroke="#4B5563" strokeWidth="0.5" />

            {/* === WHEELS - Realistic with Treads === */}

            {/* Rear Wheel (Left side - back of car) */}
            <g className="animate-wheel-spin" style={{ transformOrigin: '50px 68px' }}>
              {/* Outer Tire with tread pattern */}
              <circle cx="50" cy="68" r="13" fill="#0a0a0a" />

              {/* Tire treads - grooves */}
              <circle cx="50" cy="68" r="12.5" fill="none" stroke="#1a1a1a" strokeWidth="1" strokeDasharray="3 2" />
              <circle cx="50" cy="68" r="11.5" fill="none" stroke="#252525" strokeWidth="0.5" />

              {/* Tire sidewall */}
              <circle cx="50" cy="68" r="10" fill="#1F2937" />
              <circle cx="50" cy="68" r="9" fill="#111827" />

              {/* Rim - outer chrome ring */}
              <circle cx="50" cy="68" r="7.5" fill="none" stroke="#9CA3AF" strokeWidth="1.5" />

              {/* Rim base */}
              <circle cx="50" cy="68" r="6.5" fill="#374151" />
              <circle cx="50" cy="68" r="5.5" fill="#4B5563" />

              {/* 5-spoke alloy design */}
              <g fill="#6B7280">
                <path d="M50 62 L51.5 66 L48.5 66 Z" />
                <path d="M55 65 L52 68 L54 70 Z" />
                <path d="M53 73 L50 70 L51.5 70 Z" />
                <path d="M47 73 L48.5 70 L50 70 Z" />
                <path d="M45 65 L48 68 L46 70 Z" />
              </g>

              {/* Center hub */}
              <circle cx="50" cy="68" r="3" fill="#9CA3AF" />
              <circle cx="50" cy="68" r="1.8" fill="#D1D5DB" />

              {/* Center cap logo */}
              <circle cx="50" cy="68" r="0.8" fill="#374151" />
            </g>

            {/* Front Wheel (Right side - front of car) */}
            <g className="animate-wheel-spin" style={{ transformOrigin: '138px 68px' }}>
              {/* Outer Tire with tread pattern */}
              <circle cx="138" cy="68" r="13" fill="#0a0a0a" />

              {/* Tire treads - grooves */}
              <circle cx="138" cy="68" r="12.5" fill="none" stroke="#1a1a1a" strokeWidth="1" strokeDasharray="3 2" />
              <circle cx="138" cy="68" r="11.5" fill="none" stroke="#252525" strokeWidth="0.5" />

              {/* Tire sidewall */}
              <circle cx="138" cy="68" r="10" fill="#1F2937" />
              <circle cx="138" cy="68" r="9" fill="#111827" />

              {/* Rim - outer chrome ring */}
              <circle cx="138" cy="68" r="7.5" fill="none" stroke="#9CA3AF" strokeWidth="1.5" />

              {/* Rim base */}
              <circle cx="138" cy="68" r="6.5" fill="#374151" />
              <circle cx="138" cy="68" r="5.5" fill="#4B5563" />

              {/* 5-spoke alloy design */}
              <g fill="#6B7280">
                <path d="M138 62 L139.5 66 L136.5 66 Z" />
                <path d="M143 65 L140 68 L142 70 Z" />
                <path d="M141 73 L138 70 L139.5 70 Z" />
                <path d="M135 73 L136.5 70 L138 70 Z" />
                <path d="M133 65 L136 68 L134 70 Z" />
              </g>

              {/* Center hub */}
              <circle cx="138" cy="68" r="3" fill="#9CA3AF" />
              <circle cx="138" cy="68" r="1.8" fill="#D1D5DB" />

              {/* Center cap logo */}
              <circle cx="138" cy="68" r="0.8" fill="#374151" />
            </g>

            {/* === EXHAUST PIPE (Rear - Left Side) === */}
            <ellipse cx="12" cy="66" rx="4" ry="2" fill="#4B5563" />
            <ellipse cx="12" cy="66" rx="2.5" ry="1.2" fill="#374151" />

            {/* Exhaust Smoke Puffs */}
            <circle cx="4" cy="64" r="4" fill="#9CA3AF" className="animate-smoke-1" opacity="0.4" />
            <circle cx="-4" cy="62" r="5" fill="#9CA3AF" className="animate-smoke-2" opacity="0.3" />
            <circle cx="-14" cy="60" r="6" fill="#9CA3AF" className="animate-smoke-3" opacity="0.2" />

            {/* === BONNET/HOOD DETAILS (Front - Right Side) === */}
            {/* Hood Lines */}
            <line x1="130" y1="42" x2="155" y2="38" stroke="#4B5563" strokeWidth="0.5" />
            <line x1="130" y1="44" x2="160" y2="40" stroke="#4B5563" strokeWidth="0.3" />

            {/* === TRUNK/BOOT DETAILS (Rear - Left Side) === */}
            {/* Trunk Line */}
            <line x1="30" y1="44" x2="48" y2="42" stroke="#4B5563" strokeWidth="0.5" />
            {/* License Plate Area */}
            <rect x="20" y="56" width="12" height="6" rx="1" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="0.5" />
            <text x="26" y="61" fontSize="4" fill="#374151" textAnchor="middle">IND</text>

            {/* === RUNNING BOARD === */}
            <rect x="55" y="67" width="75" height="2" rx="1" fill="#4B5563" />
          </svg>
        </div>

        {/* Speed Lines */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 space-y-2 animate-speed-lines">
          <div className="w-8 h-0.5 bg-gradient-to-r from-blue-400 to-transparent rounded"></div>
          <div className="w-12 h-0.5 bg-gradient-to-r from-blue-300 to-transparent rounded ml-2"></div>
          <div className="w-6 h-0.5 bg-gradient-to-r from-blue-400 to-transparent rounded"></div>
        </div>
      </div>

      {/* Loading Message */}
      {message && (
        <p className="text-gray-600 font-medium text-sm">{message}</p>
      )}

      {/* Custom Styles */}
      <style>{`
        @keyframes car-drive {
          0% { left: -20%; }
          100% { left: 100%; }
        }

        @keyframes car-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }

        @keyframes wheel-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes road-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-192px); }
        }

        @keyframes smoke-1 {
          0% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          100% { transform: translate(-15px, -8px) scale(1.5); opacity: 0; }
        }

        @keyframes smoke-2 {
          0% { transform: translate(0, 0) scale(1); opacity: 0.3; }
          100% { transform: translate(-20px, -12px) scale(1.8); opacity: 0; }
        }

        @keyframes smoke-3 {
          0% { transform: translate(0, 0) scale(1); opacity: 0.2; }
          100% { transform: translate(-25px, -15px) scale(2); opacity: 0; }
        }

        @keyframes speed-lines {
          0%, 100% { opacity: 0.3; transform: translateX(0) translateY(-50%); }
          50% { opacity: 0.8; transform: translateX(-10px) translateY(-50%); }
        }

        @keyframes headlight {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes progress-circle {
          0% { stroke-dasharray: 0, 126; }
          50% { stroke-dasharray: 80, 126; }
          100% { stroke-dasharray: 126, 126; }
        }

        .animate-car-drive {
          animation: car-drive 3s linear infinite;
        }

        .animate-car-bounce {
          animation: car-bounce 0.3s ease-in-out infinite;
        }

        .animate-wheel-spin {
          animation: wheel-spin 0.4s linear infinite;
        }

        .animate-road-scroll {
          animation: road-scroll 1s linear infinite;
        }

        .animate-smoke-1 {
          animation: smoke-1 0.8s ease-out infinite;
        }

        .animate-smoke-2 {
          animation: smoke-2 1s ease-out infinite;
          animation-delay: 0.2s;
        }

        .animate-smoke-3 {
          animation: smoke-3 1.2s ease-out infinite;
          animation-delay: 0.4s;
        }

        .animate-speed-lines {
          animation: speed-lines 0.5s ease-in-out infinite;
        }

        .animate-headlight {
          animation: headlight 0.5s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }

        .animate-progress-circle {
          animation: progress-circle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Circular Loader with Car Icon
export const CircularCarLoader = ({ size = 60, message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-6">
    <div className="relative" style={{ width: size, height: size }}>
      {/* Spinning outer ring */}
      <svg className="w-full h-full animate-spin" viewBox="0 0 50 50">
        <circle
          cx="25"
          cy="25"
          r="22"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="3"
        />
        <circle
          cx="25"
          cy="25"
          r="22"
          fill="none"
          stroke="#3B82F6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="70, 138"
        />
      </svg>

      {/* Car icon in center */}
      <div className="absolute inset-0 flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-blue-600" fill="currentColor">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
        </svg>
      </div>
    </div>
    {message && (
      <p className="mt-3 text-gray-600 font-medium text-sm">{message}</p>
    )}
  </div>
);

// Racing Car Loader - Fast moving effect
export const RacingCarLoader = ({ message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center py-6">
    <div className="relative w-72 h-16 overflow-hidden bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded-xl">
      {/* Track lines */}
      <div className="absolute inset-0 flex items-center">
        <div className="flex space-x-4 animate-track-scroll">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="w-8 h-1 bg-gray-400 rounded flex-shrink-0"></div>
          ))}
        </div>
      </div>

      {/* Racing car */}
      <div className="absolute top-1/2 transform -translate-y-1/2 animate-race-car">
        <svg viewBox="0 0 80 35" className="w-20 h-10" fill="none">
          {/* Car body - sports car style */}
          <path
            d="M5 20 L10 12 L25 8 L55 8 L70 12 L75 20 L75 28 L5 28 L5 20Z"
            fill="#EF4444"
            stroke="#B91C1C"
            strokeWidth="1"
          />
          {/* Windshield */}
          <path d="M22 12 L28 9 L50 9 L56 12" fill="#1F2937" />
          {/* Racing stripe */}
          <rect x="20" y="15" width="40" height="3" fill="#FDE047" />
          {/* Wheels */}
          <g className="animate-wheel-fast" style={{ transformOrigin: '18px 28px' }}>
            <circle cx="18" cy="28" r="7" fill="#1F2937" />
            <circle cx="18" cy="28" r="4" fill="#4B5563" />
          </g>
          <g className="animate-wheel-fast" style={{ transformOrigin: '62px 28px' }}>
            <circle cx="62" cy="28" r="7" fill="#1F2937" />
            <circle cx="62" cy="28" r="4" fill="#4B5563" />
          </g>
          {/* Speed flames */}
          <path d="M2 22 L-5 20 L0 22 L-8 24 L2 24" fill="#F97316" className="animate-flames" />
        </svg>
      </div>
    </div>

    {message && (
      <p className="mt-4 text-gray-600 font-medium text-sm">{message}</p>
    )}

    <style>{`
      @keyframes track-scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-128px); }
      }

      @keyframes race-car {
        0% { left: -25%; }
        100% { left: 100%; }
      }

      @keyframes wheel-fast {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes flames {
        0%, 100% { opacity: 1; transform: scaleX(1); }
        50% { opacity: 0.7; transform: scaleX(1.2); }
      }

      .animate-track-scroll {
        animation: track-scroll 0.5s linear infinite;
      }

      .animate-race-car {
        animation: race-car 2s linear infinite;
      }

      .animate-wheel-fast {
        animation: wheel-fast 0.2s linear infinite;
      }

      .animate-flames {
        animation: flames 0.1s ease-in-out infinite;
      }
    `}</style>
  </div>
);

// Simple Spinner Alternative
export const CarSpinner = ({ className = '' }) => (
  <div className={`inline-flex items-center ${className}`}>
    <svg className="animate-spin h-5 w-5 text-blue-600 mr-2" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  </div>
);

// Full Page Loader
export const FullPageLoader = ({ message = 'Loading...' }) => (
  <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center">
    <CarLoader size="large" message={message} />
  </div>
);

// Inline Loader for buttons/cards
export const InlineLoader = ({ text = 'Loading' }) => (
  <div className="flex items-center justify-center space-x-2">
    <div className="flex space-x-1">
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
    </div>
    <span className="text-sm text-gray-600">{text}</span>
  </div>
);

// Skeleton Loader for Cards
export const CardSkeleton = () => (
  <div className="bg-white rounded-xl shadow-sm p-4 animate-pulse">
    <div className="h-40 bg-gray-200 rounded-lg mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
    <div className="flex justify-between items-center">
      <div className="h-6 bg-gray-200 rounded w-20"></div>
      <div className="h-8 bg-gray-200 rounded w-24"></div>
    </div>
  </div>
);

export default CarLoader;
